import { openExternalUrl } from "@/lib/open-url";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { isVideoUrl } from "@/lib/url-utils";
import { VideoLinkPreview } from "./VideoLinkPreview";

interface LinkPreviewProps {
  preview: {
    url: string;
    title?: string | null;
    description?: string | null;
    image_url?: string | null;
    favicon_url?: string | null;
    site_name?: string | null;
  };
}

export function LinkPreview({ preview }: LinkPreviewProps) {
  const { url, title, description, image_url, favicon_url, site_name } =
    preview;

  const hostname = (() => {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  })();

  const titleIsUrl =
    typeof title === "string" &&
    (title.startsWith("http://") || title.startsWith("https://"));

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    void openExternalUrl(url);
  };

  if (isVideoUrl(url)) {
    return <VideoLinkPreview preview={preview} />;
  }

  // Don't render if we have no useful metadata
  if (!title && !description && !image_url) {
    return null;
  }

  return (
    <Button
      type="button"
      variant="ghost"
      onClick={handleClick}
      className={cn(
        "mt-2 flex h-auto max-w-md gap-3 rounded-lg p-3 text-left",
        "bg-transparent hover:bg-foreground/[0.04]",
      )}
    >
      {image_url && (
        <div className="shrink-0 w-20 h-20 rounded overflow-hidden bg-muted/30">
          <img
            src={image_url}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => {
              // Hide broken images
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        </div>
      )}
      <div className="flex-1 min-w-0 overflow-hidden">
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-0.5">
          {favicon_url && (
            <img
              src={favicon_url}
              alt=""
              className="w-3.5 h-3.5 rounded-sm"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          )}
          <span className="truncate">{site_name || hostname}</span>
        </div>
        {title && (
          <p
            className={cn(
              "text-sm font-medium text-foreground truncate",
              titleIsUrl && "underline",
            )}
          >
            {title}
          </p>
        )}
        {description && (
          <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
            {description}
          </p>
        )}
      </div>
    </Button>
  );
}
